import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  Circle,
  BookOpen,
  Lightbulb,
  ShieldCheck,
  MessageSquare,
  FileText,
  ChevronRight,
  Trophy,
  LogOut,
} from 'lucide-react';
import { useAuth } from '../lib/use-auth';
import { supabase } from '../lib/supabase';
import Logo from '../components/Logo';

const ALL_MODULE_IDS = ['intro', 'appraisal_pitch', 'safety', 'objections', 'follow_up'];

function ScriptBlock({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-l-2 border-primary/40 bg-primary/5 rounded-r-sm px-4 py-3 my-3 text-sm text-on-surface italic">
      {children}
    </div>
  );
}

function InfoBox({
  title,
  children,
  variant = 'neutral',
}: {
  title?: string;
  children: React.ReactNode;
  variant?: 'neutral' | 'warning' | 'success';
}) {
  const styles = {
    neutral: 'bg-surface-container border-outline-variant/30 text-on-surface',
    warning: 'bg-amber-50 border-amber-200 text-amber-900',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  };
  return (
    <div className={`border rounded-sm px-4 py-3 my-3 text-sm ${styles[variant]}`}>
      {title && <p className="font-bold mb-1">{title}</p>}
      {children}
    </div>
  );
}

function ObjectionRow({ objection, response }: { objection: string; response: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-outline-variant/20 rounded-sm overflow-hidden mb-2">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-surface-container-low transition-colors"
      >
        <span className="text-sm font-medium text-on-surface">&ldquo;{objection}&rdquo;</span>
        <ChevronRight
          size={14}
          className={`shrink-0 text-on-surface-variant transition-transform ${open ? 'rotate-90' : ''}`}
        />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 bg-surface-container-low border-t border-outline-variant/20">
          <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant mb-2">
            Suggested Response
          </p>
          <p className="text-sm text-on-surface">{response}</p>
        </div>
      )}
    </div>
  );
}

const MODULES = [
  {
    id: 'intro',
    label: 'What is SiteYield?',
    icon: BookOpen,
    duration: '5 min',
    content: (
      <div className="space-y-4 text-sm text-on-surface leading-relaxed">
        <p>
          SiteYield is a property development feasibility platform. It analyses a property&apos;s
          development potential — subdivisions, townhouses, units — using council zoning rules,
          comparable sales data, and construction cost estimates.
        </p>
        <p>
          For agents, it&apos;s a competitive edge in vendor appraisals. Instead of presenting a single
          residential price estimate, you can show the vendor the full picture: what a developer would
          pay, why, and what drives that number.
        </p>
        <h3 className="font-semibold text-primary mt-6 mb-2">What the analysis covers</h3>
        <ul className="list-disc list-inside space-y-2">
          <li><strong>Zoning &amp; overlays</strong> — council-permitted uses, minimum lot sizes, height limits, setbacks</li>
          <li><strong>Development scenarios</strong> — indicative yield (number of lots/dwellings), GDV (Gross Development Value)</li>
          <li><strong>Cost model</strong> — construction, holding, and disposal costs benchmarked to the suburb</li>
          <li><strong>Net profit &amp; margin</strong> — residual land value and developer return at various risk thresholds</li>
        </ul>
        <h3 className="font-semibold text-primary mt-6 mb-2">Why it matters to vendors</h3>
        <p>
          Development-aware buyers pay a premium that most residential comps don&apos;t capture. If a
          vendor&apos;s block is a viable development site and their agent hasn&apos;t identified it,
          they&apos;re likely leaving money on the table. SiteYield makes that value visible before the
          property hits the market.
        </p>
        <InfoBox variant="success" title="Your positioning">
          You&apos;re not just selling a house — you&apos;re marketing a development opportunity.
          SiteYield gives you the data to back that claim and attract the right buyers.
        </InfoBox>
      </div>
    ),
  },
  {
    id: 'appraisal_pitch',
    label: 'The Appraisal Pitch',
    icon: Lightbulb,
    duration: '8 min',
    content: (
      <div className="space-y-4 text-sm text-on-surface leading-relaxed">
        <p>
          The right moment to introduce SiteYield is during the appraisal meeting itself — after
          you&apos;ve covered the residential comps and before you present your recommended campaign price.
        </p>
        <h3 className="font-semibold text-primary mt-6 mb-2">The three-step introduction</h3>
        <div className="space-y-3">
          {[
            {
              n: 1,
              title: 'Signal the difference',
              script: '"Before I give you our recommended price, I want to show you something most agents don\'t include in their appraisals..."',
            },
            {
              n: 2,
              title: 'Show the analysis',
              script: '"SiteYield has analysed your property\'s development potential based on your council\'s zoning rules. Based on the block size and zoning, we\'re seeing a potential for [X townhouses / dual occupancy / subdivision]. A developer could pay up to $X for your site."',
            },
            {
              n: 3,
              title: 'Connect to campaign strategy',
              script: '"Because we know this, we\'re going to market specifically to development-aware buyers. That widens the pool and creates competitive tension at the top end of the market. I\'ll leave you with a copy of this analysis."',
            },
          ].map(({ n, title, script }) => (
            <div key={n} className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-on-primary text-xs font-bold flex items-center justify-center">
                {n}
              </span>
              <div>
                <p className="font-semibold text-on-surface mb-1">{title}</p>
                <ScriptBlock>{script}</ScriptBlock>
              </div>
            </div>
          ))}
        </div>
        <h3 className="font-semibold text-primary mt-6 mb-2">What to show on screen</h3>
        <ul className="list-disc list-inside space-y-2">
          <li>The feasibility summary (outcome badge, net profit, margin)</li>
          <li>The development scenario breakdown (yield, GDV, cost model)</li>
          <li>Comparable developer transactions in the suburb if available</li>
        </ul>
        <InfoBox title="Tip: Let the numbers land">
          After showing the analysis, pause. Don&apos;t immediately explain every figure. Let the vendor
          read it and ask a question — the silence builds interest.
        </InfoBox>
      </div>
    ),
  },
  {
    id: 'safety',
    label: 'Safety & Compliance',
    icon: ShieldCheck,
    duration: '6 min',
    content: (
      <div className="space-y-4 text-sm text-on-surface leading-relaxed">
        <p>
          SiteYield is a decision-support tool, not a substitute for professional planning or
          financial advice. Used correctly, it strengthens your appraisal. Used carelessly, it can
          create misunderstandings or expose you to liability.
        </p>
        <InfoBox variant="warning" title="What SiteYield is NOT">
          <ul className="list-disc list-inside space-y-1 mt-1">
            <li>Not a development approval or planning permit</li>
            <li>Not financial advice or a valuation</li>
            <li>Not a guarantee of development potential</li>
            <li>Not a substitute for a qualified town planner or architect</li>
          </ul>
        </InfoBox>
        <h3 className="font-semibold text-primary mt-6 mb-2">Required disclaimer</h3>
        <ScriptBlock>
          &ldquo;This analysis is generated by SiteYield and is based on publicly available zoning data
          and comparable sales. It is indicative only and does not constitute financial advice, a
          property valuation, or planning approval. We recommend the vendor consult a registered town
          planner for detailed development advice.&rdquo;
        </ScriptBlock>
        <h3 className="font-semibold text-primary mt-6 mb-2">Phrases to avoid</h3>
        <div className="space-y-2">
          <div className="flex gap-2 text-[10px] font-bold uppercase tracking-widest text-on-surface-variant pb-1">
            <span className="flex-1">Avoid saying</span>
            <span className="flex-1">Say instead</span>
          </div>
          {[
            ['"You can definitely build X townhouses here"', '"SiteYield indicates the zoning supports up to X dwellings, subject to a development application"'],
            ['"You\'ll get $X from a developer"', '"Indicative residual land value is around $X based on current comparable transactions"'],
            ['"This is approved for development"', '"The zoning permits this type of development — approval is a separate process"'],
          ].map(([avoid, safe], i) => (
            <div key={i} className="flex gap-2">
              <div className="flex-1 bg-red-50 border border-red-200 rounded-sm px-3 py-2 text-red-800">{avoid}</div>
              <div className="flex-1 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2 text-emerald-800">{safe}</div>
            </div>
          ))}
        </div>
        <h3 className="font-semibold text-primary mt-6 mb-2">If a vendor wants to rely on the numbers</h3>
        <ScriptBlock>
          &ldquo;I&apos;m glad the numbers are resonant. For a formal assessment you can take to the bank or
          use in negotiations, you&apos;d want a town planner to run a feasibility study and a licensed
          valuer to confirm the land value. Happy to recommend someone.&rdquo;
        </ScriptBlock>
      </div>
    ),
  },
  {
    id: 'objections',
    label: 'Objection Handling',
    icon: MessageSquare,
    duration: '7 min',
    content: (
      <div className="space-y-4 text-sm text-on-surface leading-relaxed">
        <p>
          Vendors will push back. Some are sceptical about the numbers. Some are excited and want
          guarantees you can&apos;t give. Tap each objection to reveal the suggested response.
        </p>
        <h3 className="font-semibold text-primary mt-4 mb-3">Common objections</h3>
        <ObjectionRow
          objection="The numbers look too optimistic"
          response="These figures are based on real comparable developer transactions in your suburb and your council's actual zoning rules — not guess work. They are indicative, which is why there's a margin range rather than a single number. The final outcome depends on the development application, which we'd always disclose to buyers."
        />
        <ObjectionRow
          objection="My previous agent never mentioned development potential"
          response="Most residential agents focus on the owner-occupier and investor markets, which is the right instinct for most properties. Where SiteYield adds value is identifying the small portion of properties where development-aware buyers would pay meaningfully more — and making sure we're marketing to them."
        />
        <ObjectionRow
          objection="Can I use this number as my asking price?"
          response="The SiteYield figure is the indicative residual land value to a developer, not a listing price. Your listing price will still be set based on residential comps and market conditions. What this does is give us ammunition to attract development buyers into the mix, which creates competition and can push the final price up."
        />
        <ObjectionRow
          objection="Is this analysis guaranteed?"
          response="No. SiteYield is a preliminary analysis tool. It tells us the development potential looks viable based on the zoning and cost model. Whether a developer proceeds depends on their own due diligence and the DA outcome. We always disclose this to buyers."
        />
        <ObjectionRow
          objection="How is this calculated — is it just a guess?"
          response="No. SiteYield uses council zoning maps, comparable developer sales, and a cost model with editable assumptions — construction rates, contingency, margin, sale prices. Those defaults are general industry guides, but every figure is a transparent assumption you can adjust if you have better local data. It's a structured feasibility calculation, not a black box."
        />
        <ObjectionRow
          objection="I don't want developers lowballing me"
          response="The goal is the opposite. By identifying your property as a development opportunity and marketing it explicitly to that buyer pool, we create competition between developers, investors, and owner-occupiers. Competition drives price up, not down. Developers who aren't informed of the potential won't bid at all."
        />
        <InfoBox title="When to escalate">
          If a vendor makes a specific claim about what they&apos;ve heard from a town planner or developer,
          don&apos;t debate the figure. Acknowledge their research:{' '}
          <em>
            &ldquo;That&apos;s useful context — our analysis is consistent with / slightly more conservative
            than that, which is intentional since we&apos;re benchmarking against completed transactions
            rather than proposed projects.&rdquo;
          </em>
        </InfoBox>
      </div>
    ),
  },
  {
    id: 'follow_up',
    label: 'Report as Follow-up Asset',
    icon: FileText,
    duration: '5 min',
    content: (
      <div className="space-y-4 text-sm text-on-surface leading-relaxed">
        <p>
          The SiteYield PDF report is one of the most effective follow-up tools you have after an
          appraisal meeting. A vendor who leaves with a tangible document remembers the conversation
          differently than one who just hears numbers.
        </p>
        <h3 className="font-semibold text-primary mt-6 mb-2">When to send the report</h3>
        <ul className="list-disc list-inside space-y-2">
          <li><strong>Same day</strong> — email within a few hours of the appraisal while the meeting is fresh</li>
          <li><strong>Not during the meeting</strong> — save the send for after you&apos;ve left so it lands as a professional follow-through</li>
          <li><strong>With the appraisal letter</strong> — attach alongside your agency agreement and property booklet</li>
        </ul>
        <h3 className="font-semibold text-primary mt-6 mb-2">Email template</h3>
        <div className="bg-surface-container border border-outline-variant/30 rounded-sm px-4 py-3 text-sm font-mono text-on-surface">
          <p className="text-on-surface-variant text-[10px] font-bold uppercase tracking-widest mb-3 font-sans">Email template</p>
          <p className="mb-2"><strong>Subject:</strong> Your SiteYield property analysis — [address]</p>
          <p className="mb-2">Hi [Name],</p>
          <p className="mb-2">Thank you for your time today. As promised, I&apos;ve attached the SiteYield feasibility analysis for [address].</p>
          <p className="mb-2">The key takeaways are on page 1 — development potential, indicative net profit range, and market positioning. Page 2 covers the zoning detail and cost model in more depth.</p>
          <p className="mb-2">This is preliminary analysis and not a formal valuation, but it gives us a clear view of the buyer pool we&apos;ll be targeting and why.</p>
          <p className="mb-2">I&apos;ll follow up [day] to discuss next steps. In the meantime, please don&apos;t hesitate to call if you have questions.</p>
          <p>Kind regards,<br />[Your name]</p>
        </div>
        <h3 className="font-semibold text-primary mt-6 mb-2">Referencing in follow-up calls</h3>
        <ScriptBlock>
          &ldquo;Did you get a chance to look at the SiteYield report I sent through? ... The development
          scenario on page 2 is what I&apos;d want to highlight when we&apos;re talking to buyers. That&apos;s
          what creates the competition we need.&rdquo;
        </ScriptBlock>
        <h3 className="font-semibold text-primary mt-6 mb-2">If the vendor is still deciding</h3>
        <ScriptBlock>
          &ldquo;I wanted to check in — have you had any more thoughts since we met? The SiteYield analysis
          I sent gives you a solid basis for a price discussion whenever you&apos;re ready.&rdquo;
        </ScriptBlock>
        <InfoBox variant="success" title="Maximise the report's impact">
          Print a copy and leave it with the vendor at the meeting — then follow up by email so they
          have a digital version. Two touchpoints from a single asset.
        </InfoBox>
      </div>
    ),
  },
];

export default function AgentTraining() {
  const navigate = useNavigate();
  const { profile, session, loading, signOut } = useAuth();

  const [completed, setCompleted] = useState<Set<string>>(new Set());
  const [activeId, setActiveId] = useState(MODULES[0]!.id);
  const [saving, setSaving] = useState(false);
  const [allDone, setAllDone] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Load prior progress once session is available
  useEffect(() => {
    if (!session || hydrated) return;
    setHydrated(true);
    supabase
      .from('agent_training_events')
      .select('module')
      .eq('user_id', session.user.id)
      .eq('event_type', 'module_completed')
      .then(({ data }) => {
        if (data) {
          const done = new Set(data.map((r) => r.module).filter(Boolean) as string[]);
          setCompleted(done);
          if (done.size === ALL_MODULE_IDS.length) setAllDone(true);
        }
      });
  }, [session, hydrated]);

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!session || (profile && profile.role !== 'agent' && profile.role !== 'admin')) {
    navigate('/account?access=tool_denied', { replace: true });
    return null;
  }

  const activeModule = MODULES.find((m) => m.id === activeId)!;
  const progress = Math.round((completed.size / ALL_MODULE_IDS.length) * 100);

  async function markComplete(moduleId: string) {
    if (completed.has(moduleId) || saving || !session) return;
    setSaving(true);

    const next = new Set(completed);
    next.add(moduleId);
    setCompleted(next);

    const nowAllDone = next.size === ALL_MODULE_IDS.length;
    if (nowAllDone) {
      setAllDone(true);
    } else {
      const nextIdx = MODULES.findIndex((m) => m.id === moduleId) + 1;
      if (nextIdx < MODULES.length) setActiveId(MODULES[nextIdx]!.id);
    }

    try {
      await supabase.from('agent_training_events').insert({
        user_id: session.user.id,
        event_type: 'module_completed',
        module: moduleId,
      });
      if (nowAllDone) {
        await supabase.from('agent_training_events').insert({
          user_id: session.user.id,
          event_type: 'training_completed',
          module: null,
        });
      }
    } catch {
      // silently ignore — UI already updated
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-surface">
      <nav className="border-b border-outline-variant/20 bg-surface/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="hover:opacity-80 transition-opacity">
            <Logo className="h-9 text-primary" />
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/account"
              className="text-xs font-bold uppercase tracking-widest text-on-surface-variant hover:text-primary transition-colors"
            >
              Dashboard
            </Link>
            <button
              onClick={signOut}
              className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-on-surface-variant hover:text-primary transition-colors"
            >
              <LogOut size={14} />
              <span className="hidden sm:block">Sign out</span>
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-primary mb-1">Agent Training</h1>
            <p className="text-sm text-on-surface-variant">
              Master the SiteYield appraisal conversation in five modules.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Progress</p>
              <p className="text-sm font-bold text-primary">{completed.size}/{ALL_MODULE_IDS.length} modules</p>
            </div>
            <div className="w-24 h-2 bg-surface-container-high rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        {allDone && (
          <div className="mb-6 flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-sm px-4 py-3">
            <Trophy size={18} className="text-emerald-600 shrink-0" />
            <p className="text-sm font-medium text-emerald-800">
              Training complete — you&apos;re ready to run the SiteYield appraisal conversation.
            </p>
          </div>
        )}

        <div className="flex gap-6 items-start">
          {/* Sidebar */}
          <aside className="hidden md:flex flex-col gap-1 w-56 shrink-0">
            {MODULES.map((m) => {
              const Icon = m.icon;
              const done = completed.has(m.id);
              const active = m.id === activeId;
              return (
                <button
                  key={m.id}
                  onClick={() => setActiveId(m.id)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-sm text-left text-xs font-bold uppercase tracking-widest transition-colors ${
                    active
                      ? 'bg-primary text-on-primary'
                      : 'text-on-surface-variant hover:text-primary hover:bg-surface-container'
                  }`}
                >
                  {done
                    ? <CheckCircle2 size={14} className={active ? 'text-on-primary' : 'text-emerald-500'} />
                    : <Circle size={14} className={active ? 'text-on-primary/60' : 'text-on-surface-variant/40'} />}
                  <Icon size={13} className="shrink-0" />
                  <span className="leading-tight">{m.label}</span>
                </button>
              );
            })}
          </aside>

          {/* Mobile tabs */}
          <div className="md:hidden w-full mb-4 flex gap-1 overflow-x-auto pb-1">
            {MODULES.map((m) => {
              const done = completed.has(m.id);
              const active = m.id === activeId;
              return (
                <button
                  key={m.id}
                  onClick={() => setActiveId(m.id)}
                  className={`shrink-0 px-3 py-2 rounded-sm text-[10px] font-bold uppercase tracking-widest whitespace-nowrap flex items-center gap-1.5 transition-colors ${
                    active ? 'bg-primary text-on-primary' : 'bg-surface-container text-on-surface-variant'
                  }`}
                >
                  {done && <CheckCircle2 size={11} />}
                  {m.label}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="border border-outline-variant/20 rounded-sm">
              <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-outline-variant/20 bg-surface-container">
                <div className="flex items-center gap-2.5">
                  {(() => { const Icon = activeModule.icon; return <Icon size={16} className="text-primary shrink-0" />; })()}
                  <h2 className="text-sm font-semibold text-on-surface">{activeModule.label}</h2>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant shrink-0">
                  {activeModule.duration}
                </span>
              </div>

              <div className="px-5 py-5">{activeModule.content}</div>

              <div className="px-5 py-4 border-t border-outline-variant/20 flex items-center justify-between gap-4">
                {completed.has(activeModule.id) ? (
                  <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-emerald-600">
                    <CheckCircle2 size={14} />
                    Completed
                  </span>
                ) : (
                  <button
                    onClick={() => markComplete(activeModule.id)}
                    disabled={saving}
                    className="flex items-center gap-2 bg-primary text-on-primary px-4 py-2.5 rounded-sm text-xs font-bold uppercase tracking-widest hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Saving...' : 'Mark Complete'}
                    {!saving && <ChevronRight size={13} />}
                  </button>
                )}
                <div className="flex items-center gap-1">
                  {MODULES.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => setActiveId(m.id)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        completed.has(m.id) ? 'bg-emerald-400' : m.id === activeId ? 'bg-primary' : 'bg-outline-variant/40'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
